// src/components/onboarding/CostTransparencyScreen.js
// Onboarding screen 2 (0-indexed) — shown after profile + modalities, before ReadyScreen.
// Transparent cost breakdown so practitioners have zero surprises after signing up.

import React from 'react';
import { CheckCircle, Shield, Zap } from 'lucide-react';

const INCLUDED = [
  'Website hosting & builder',
  'Booking system & calendar sync',
  'Client management & session notes',
  'Intake forms & messaging system',
  'Directory listing',
  'Email & SMS notifications',
  'AI practice insights (Premier plan)',
  'AI session prep (Premier plan)',
];

const CostTransparencyScreen = ({ onContinue }) => {
  return (
    <div>
      {/* Header */}
      <div className="text-center mb-7">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          What It Costs to Run Your Practice
        </h2>
        <p className="text-sm text-gray-500 leading-relaxed">
          Your Divine Frameworks subscription is your only required cost.
          Everything else is optional.
        </p>
      </div>

      <div className="space-y-4">

        {/* Section 1: Included */}
        <div className="bg-green-50 border border-green-200 rounded-xl p-5">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-7 h-7 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
              <CheckCircle className="w-4 h-4 text-green-600" />
            </div>
            <h3 className="font-semibold text-gray-900 text-sm">Included with your plan</h3>
            <span className="ml-auto text-xs font-medium px-2 py-0.5 bg-green-100 text-green-700 rounded-full">
              Included
            </span>
          </div>
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
            {INCLUDED.map(item => (
              <li key={item} className="flex items-center gap-2 text-sm text-gray-700">
                <CheckCircle className="w-3.5 h-3.5 text-green-500 flex-shrink-0" />
                {item}
              </li>
            ))}
          </ul>
        </div>

        {/* Section 2: Stripe */}
        <div className="bg-white border border-gray-200 rounded-xl p-5">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-7 h-7 rounded-full bg-blue-50 flex items-center justify-center flex-shrink-0">
              <Shield className="w-4 h-4 text-blue-500" />
            </div>
            <h3 className="font-semibold text-gray-900 text-sm">Payment processing</h3>
          </div>

          <p className="text-sm text-gray-600 mb-3 leading-relaxed">
            When clients pay online, Stripe processes the payment securely. They charge{' '}
            <span className="font-semibold text-gray-800">2.9% + $0.30 per transaction</span> — deducted
            automatically before the rest reaches your bank.
          </p>

          {/* Math callout */}
          <div className="bg-blue-50 rounded-lg px-4 py-3 mb-3 text-center">
            <p className="text-sm text-blue-800">
              On a <strong>$100 session</strong>, Stripe takes{' '}
              <strong>$3.20</strong> — you receive{' '}
              <strong>$96.80</strong>
            </p>
          </div>

          <p className="text-xs text-gray-500 leading-relaxed">
            Stripe handles payment disputes, refunds, and tax documentation so you don't have to.
            If you only see clients in person and collect payment yourself, you can skip Stripe —
            online booking payments require it.
          </p>
        </div>

        {/* Section 3: Optional integrations */}
        <div className="bg-white border border-gray-200 rounded-xl p-5">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-7 h-7 rounded-full bg-purple-50 flex items-center justify-center flex-shrink-0">
              <Zap className="w-4 h-4 text-purple-500" />
            </div>
            <h3 className="font-semibold text-gray-900 text-sm">Optional add-ons</h3>
            <span className="ml-auto text-xs font-medium px-2 py-0.5 bg-gray-100 text-gray-500 rounded-full">
              You choose
            </span>
          </div>

          <div className="space-y-3">
            {/* Zoom */}
            <div className="flex items-start gap-3 py-3 border-b border-gray-100">
              <span className="text-base mt-0.5">🎥</span>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-sm font-medium text-gray-800">Session Recording &amp; AI Transcription</span>
                  <span className="text-xs px-1.5 py-0.5 bg-gray-100 text-gray-500 rounded font-medium">Optional</span>
                </div>
                <p className="text-xs text-gray-700 leading-relaxed">
                  Requires <a href="https://zoom.us/pricing" target="_blank" rel="noopener noreferrer" className="text-purple-600 hover:text-purple-700 underline font-medium">Zoom Pro</a> (~$13/month, paid to Zoom). Free Zoom works for 1-on-1 sessions of any length, but Zoom Pro is needed for group sessions over 40 minutes, automatic recordings, and AI session summaries.
                </p>
              </div>
            </div>

            {/* ConvertKit */}
            <div className="flex items-start gap-3 pt-1">
              <span className="text-base mt-0.5">📧</span>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-sm font-medium text-gray-800">Advanced Email Marketing</span>
                  <span className="text-xs px-1.5 py-0.5 bg-gray-100 text-gray-500 rounded font-medium">Optional</span>
                </div>
                <p className="text-xs text-gray-700 leading-relaxed">
                  Connect ConvertKit for automated welcome emails and scheduled campaigns.
                  Free for up to 10,000 subscribers. Without it, you can still send newsletters
                  directly from Divine Frameworks.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Playbook link */}
      <p className="text-center text-xs text-gray-400 mt-5">
        Want more details?{' '}
        <a
          href="/business-playbook/practice-costs"
          target="_blank"
          rel="noopener noreferrer"
          className="text-purple-600 hover:text-purple-700 underline transition-colors"
        >
          Read the full breakdown in your Business Playbook →
        </a>
      </p>

      {/* Continue */}
      <button
        onClick={onContinue}
        className="mt-5 w-full py-3 px-6 rounded-xl text-white font-semibold text-sm transition-all"
        style={{
          background: 'linear-gradient(135deg, #7c3aed, #22d3ee, #5b21b6, #bb9749, #7c3aed)',
          backgroundSize: '300% 300%',
          animation: 'auroraShift 14s ease infinite',
        }}
      >
        Got it, let's build my site →
      </button>
    </div>
  );
};

export default CostTransparencyScreen;
